/**
 * Le bloc « Tournée » du briefing d'une intervention (lot U1d, ADR-0016).
 *
 * L'intervention ne recopie rien de sa tournée : « occurrence 2 sur 4 », la remarque permanente, le n° de commande et la durée
 * se LISENT de la tournée (convention n° 9). Ici on peut déplier la tournée (ses occurrences avec leur état, ses machines, ses
 * techniciens habituels), la modifier — libellé, n° de commande, durée, remarque — ou l'arrêter. L'arrêt est définitif et dit
 * AVANT combien d'interventions à venir il annule. Un refus du serveur (409 : la tournée a changé sous nos yeux) relit la tournée
 * puis se dit, jamais l'inverse.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { ApiError, api, type BriefingIntervention, type Technicien, type Tournee, type TourneeRythmeChange } from '../api/client';
import { aujourdhuiLocal } from '../domaine/interventions';
import { jourFr } from '../domaine/planning';
import { lienIntervention, phraseArretIntervention, phraseOccurrenceSurTotal, phraseResultatArret, LIBELLE_ETAT_OCCURRENCE } from '../domaine/planning-interventions';
import {
  corpsModificationTournee,
  libelleFrequence,
  nomMachineTournee,
  phraseEffetComposition,
  phraseResultatModification,
  saisieDepuisTournee,
  type SaisieTournee,
} from '../domaine/planning-tournees';
import {
  RYTHMES_RAPIDES,
  corpsChangementRythme,
  lireRythme,
  phraseEffetRythme,
  phraseResultatRythme,
  saisieRythmeDepart,
  type SaisieRythme,
} from '../domaine/rythme-tournee';
import { ErreurFormulaire, Panneau } from './Formulaire';

export function BlocTourneeIntervention({
  tour,
  interventionId,
  peutGerer,
  revision,
  onChange,
}: {
  tour: NonNullable<BriefingIntervention['tour']>;
  interventionId: string;
  peutGerer: boolean;
  /** La révision de l'intervention lue : elle change à chaque écriture, la tournée dépliée se relit avec elle. */
  revision: number;
  /** Après une écriture : la page relit la semaine, la liste et le briefing. */
  onChange: () => void;
}) {
  const [ouvert, setOuvert] = useState(false);
  const [detail, setDetail] = useState<Tournee | null>(null);
  const [erreurLecture, setErreurLecture] = useState<string | null>(null);
  const [modification, setModification] = useState<SaisieTournee | null>(null);
  /** Changer le rythme : le panneau, et — une fois fait — l'accès à la première occurrence de la NOUVELLE tournée. */
  const [rythme, setRythme] = useState<SaisieRythme | null>(null);
  const [nouvelleTournee, setNouvelleTournee] = useState<{ interventionId: string; plannedDate: string | null } | null>(null);
  /** Les choix du panneau : les machines du site (celles de la tournée comprises) et les techniciens actifs. */
  const [choixMachines, setChoixMachines] = useState<readonly { id: string; nom: string }[]>([]);
  const [choixTechniciens, setChoixTechniciens] = useState<readonly Technicien[]>([]);
  const [enCours, setEnCours] = useState(false);
  const [succes, setSucces] = useState<string | null>(null);
  const [erreur, setErreur] = useState<ApiError | Error | null>(null);

  useEffect(() => {
    if (!ouvert) return;
    let annule = false;
    api
      .tournee(tour.id)
      .then((t) => {
        if (annule) return;
        setDetail(t);
        setErreurLecture(null);
      })
      .catch((e: unknown) => {
        if (!annule) setErreurLecture(e instanceof ApiError ? e.message : 'Lecture de la tournée impossible.');
      });
    return () => {
      annule = true;
    };
  }, [ouvert, tour.id, revision]);

  /** Un geste sur la tournée : la tournée est relue AVANT d'annoncer un refus de concurrence (jamais une erreur posée puis effacée). */
  const agir = async (action: () => Promise<unknown>, phrase: (r: unknown) => string) => {
    setEnCours(true);
    setErreur(null);
    setSucces(null);
    setNouvelleTournee(null);
    try {
      const r = await action();
      setModification(null);
      setRythme(null);
      setSucces(phrase(r));
      onChange();
      if (ouvert) setDetail(await api.tournee(tour.id));
    } catch (e) {
      const err = e instanceof ApiError || e instanceof Error ? e : new Error('Action impossible.');
      if (err instanceof ApiError && err.status === 409) {
        setModification(null);
        setRythme(null);
        onChange();
        if (ouvert) setDetail(await api.tournee(tour.id).catch(() => null));
      }
      setErreur(err);
    } finally {
      setEnCours(false);
    }
  };

  /**
   * Le détail se lit à la demande : un geste sur la tournée n'exige pas de l'avoir dépliée avant. Il se RELIT à chaque geste : le
   * jeton de la tournée avance aussi quand un autre geste l'écrit (le bloc du montant, plus bas), et un détail gardé en mémoire enverrait une révision périmée.
   */
  const detailLu = async () => {
    try {
      const d = await api.tournee(tour.id);
      setDetail(d);
      return d;
    } catch (e) {
      setErreur(e instanceof Error ? e : new Error('Lecture de la tournée impossible.'));
      return null;
    }
  };

  const ouvrirModification = async () => {
    setErreur(null);
    setSucces(null);
    const d = await detailLu();
    if (!d) return;
    try {
      const [machines, techniciens] = await Promise.all([api.assets({ siteId: d.siteId, take: 200 }), api.techniciensPlanning()]);
      const noms = new Map<string, string>(d.assets.map((a) => [a.assetId, a.label]));
      for (const m of machines.items) if (!noms.has(m.id)) noms.set(m.id, nomMachineTournee(m));
      setChoixMachines([...noms].map(([id, nom]) => ({ id, nom })).sort((a, b) => a.nom.localeCompare(b.nom, 'fr')));
      // Un habituel archivé reste proposé tant qu'il est coché : on ne le retire pas d'office.
      const habituels = new Set(d.technicians.map((t) => t.id));
      setChoixTechniciens(techniciens.items.filter((t) => !t.archived || habituels.has(t.id)));
    } catch (e) {
      setErreur(e instanceof Error ? e : new Error('Lecture des machines et des techniciens impossible.'));
      return;
    }
    setModification(saisieDepuisTournee(d));
  };

  const enregistrer = () => {
    if (!detail || !modification) return;
    const lue = corpsModificationTournee(detail, modification);
    if (!lue.ok) {
      setErreur(new Error(lue.erreur));
      return;
    }
    if (lue.corps === null) {
      setModification(null);
      return;
    }
    const corps = lue.corps;
    void agir(() => api.modifierTournee(detail.id, corps), (r) => phraseResultatModification(r as Parameters<typeof phraseResultatModification>[0]));
  };

  const ouvrirRythme = async () => {
    setErreur(null);
    setSucces(null);
    const d = await detailLu();
    if (!d) return;
    setRythme(saisieRythmeDepart(d, aujourdhuiLocal()));
  };

  const changerRythme = () => {
    if (!detail || !rythme) return;
    const lu = lireRythme(rythme, detail, aujourdhuiLocal());
    if (!lu.ok) {
      setErreur(new Error(lu.erreur));
      return;
    }
    const corps = corpsChangementRythme(detail.revision, lu);
    void agir(
      () => api.changerRythmeTournee(detail.id, { ...corps, clientKey: globalThis.crypto.randomUUID() }),
      (r) => {
        const res = r as TourneeRythmeChange;
        const premiere = res.nouvelle.occurrences[0];
        setNouvelleTournee(premiere ? { interventionId: premiere.interventionId, plannedDate: premiere.plannedDate } : null);
        return phraseResultatRythme(res);
      },
    );
  };

  const arreter = async () => {
    setErreur(null);
    setSucces(null);
    const d = await detailLu();
    if (!d) return;
    if (!window.confirm(`Arrêter la tournée « ${d.label} » ?\n\n${phraseArretIntervention(d, aujourdhuiLocal())}`)) return;
    void agir(
      () => api.arreterTournee(d.id, d.revision),
      (r) => phraseResultatArret((r as { interventionsAnnulees?: number }).interventionsAnnulees ?? 0),
    );
  };

  const effetComposition = modification && detail ? phraseEffetComposition(detail, modification) : null;
  const lectureRythme = rythme && detail ? lireRythme(rythme, detail, aujourdhuiLocal()) : null;
  const effetRythme = lectureRythme?.ok && detail ? phraseEffetRythme(detail, lectureRythme, aujourdhuiLocal()) : null;

  return (
    <div className="carte bloc-tournee">
      <h2>
        Tournée — {libelleFrequence(tour.frequencyMonths)} {tour.stopped && <span className="etat revoque">arrêtée</span>}
      </h2>
      <div className="carte-corps">
        <p>
          {tour.occurrenceNo !== null && <strong>{phraseOccurrenceSurTotal(tour.occurrenceNo, tour.occurrencesTotal)}</strong>}
          <span className="aide"> · {tour.label}</span>
        </p>
        {tour.note && (
          <p className="tournee-note observations-bon" title="La remarque de la tournée : la même sur chaque intervention">
            {tour.note}
          </p>
        )}
        <p className="aide">
          {tour.customerOrderRef && <span>N° de commande {tour.customerOrderRef} (lu de la tournée). </span>}
          {tour.durationMinutes !== null && <span>Durée prévue lue de la tournée. </span>}
          Le client, le site et les machines sont lus de la tournée : rien n’est recopié.
        </p>

        <div className="saisie-ligne planning-actions" role="group" aria-label="Gestes sur la tournée">
          <button type="button" className="secondaire" aria-pressed={ouvert} onClick={() => setOuvert(!ouvert)}>
            {ouvert ? 'Replier la tournée' : 'Voir la tournée'}
          </button>
          {peutGerer && !tour.stopped && (
            <button type="button" className="secondaire" disabled={enCours} onClick={() => void ouvrirModification()}>
              Modifier la tournée
            </button>
          )}
          {peutGerer && !tour.stopped && (
            <button type="button" className="secondaire" disabled={enCours} onClick={() => void ouvrirRythme()} title="Arrête cette tournée et en crée une autre au nouveau rythme ou au nouveau jour">
              Changer le rythme ou le jour
            </button>
          )}
          {peutGerer && !tour.stopped && (
            <button type="button" className="danger" disabled={enCours} onClick={() => void arreter()}>
              Arrêter la tournée
            </button>
          )}
        </div>

        {succes && <div className="message succes">{succes}</div>}
        {nouvelleTournee && (
          <p>
            <Link to={lienIntervention(nouvelleTournee.interventionId, nouvelleTournee.plannedDate)}>Ouvrir la première occurrence de la nouvelle tournée</Link>
          </p>
        )}
        {!modification && !rythme && <ErreurFormulaire message={erreur?.message ?? null} details={erreur instanceof ApiError ? erreur.details : undefined} />}
        {erreurLecture && <div className="message erreur">{erreurLecture}</div>}

        {ouvert && !detail && !erreurLecture && <p className="aide">Lecture de la tournée…</p>}
        {ouvert && detail && (
          <div className="tournee-detail">
            <dl className="proprietes">
              <dt>Site</dt>
              <dd>
                <Link to={`/sites/${detail.siteId}`}>{detail.siteName}</Link>
                <span className="aide"> · {detail.customerName}</span>
              </dd>
              <dt>Fréquence</dt>
              <dd>
                {detail.natureLabel}, {libelleFrequence(detail.frequencyMonths)}, depuis le {jourFr(detail.firstDay)}
              </dd>
              <dt>Machines</dt>
              <dd>{detail.assets.map((a) => a.label).join(' · ') || '—'}</dd>
              <dt>Habituellement</dt>
              <dd>{detail.technicians.length > 0 ? detail.technicians.map((t) => t.name).join(', ') : <span className="aide">personne : chaque intervention attend son équipe</span>}</dd>
            </dl>
            <ul className="tournee-occurrences">
              {detail.occurrences.map((o) => (
                <li key={o.interventionId} className={o.interventionId === interventionId ? 'courante' : ''}>
                  <Link to={lienIntervention(o.interventionId, o.plannedDate)} aria-current={o.interventionId === interventionId ? 'true' : undefined}>
                    <strong>
                      {o.no}/{detail.occurrences.length}
                    </strong>{' '}
                    {o.plannedDate ? jourFr(o.plannedDate) : 'sans date'}
                  </Link>
                  <span className={`etat ${o.status === 'DONE' ? 'actif' : o.status === 'CANCELLED' ? 'revoque' : 'attente'}`}>{LIBELLE_ETAT_OCCURRENCE[o.status]}</span>
                  <span className="aide">{o.technicianNames.length > 0 ? o.technicianNames.join(', ') : 'à affecter'}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {rythme && detail && (
        <Panneau
          titre="Changer le rythme ou le jour de la tournée"
          sousTitre="Le rythme et le jour ne se modifient pas sur place : la tournée actuelle est arrêtée et une nouvelle prend le relais, en un seul geste. Pour changer seulement le jour (passer du 15 au 25 du mois), gardez le rythme et choisissez la nouvelle date."
          onFermer={() => setRythme(null)}
          actions={
            <>
              <button type="button" className="secondaire" disabled={enCours} onClick={() => setRythme(null)}>
                Ne rien changer
              </button>
              <button type="button" disabled={enCours || lectureRythme?.ok !== true} onClick={changerRythme}>
                Arrêter et recréer la tournée
              </button>
            </>
          }
        >
          <div className="champ">
            <label htmlFor="tournee-rythme-mois">Rythme : tous les … mois</label>
            <input
              id="tournee-rythme-mois"
              type="text"
              inputMode="numeric"
              maxLength={3}
              value={rythme.moisTexte}
              placeholder={`${detail.frequencyMonths} aujourd’hui`}
              onChange={(e) => setRythme({ ...rythme, moisTexte: e.target.value })}
            />
            <div className="saisie-ligne" role="group" aria-label="Rythmes courants">
              {RYTHMES_RAPIDES.map((m) => (
                <button key={m} type="button" className="secondaire" onClick={() => setRythme({ ...rythme, moisTexte: String(m) })}>
                  {libelleFrequence(m)}
                </button>
              ))}
            </div>
          </div>
          <div className="champ">
            <label htmlFor="tournee-rythme-jour">Première occurrence de la nouvelle tournée</label>
            <input id="tournee-rythme-jour" type="date" value={rythme.premierJour} onChange={(e) => setRythme({ ...rythme, premierJour: e.target.value })} />
            <p className="aide">Proposé : le jour de la prochaine occurrence à faire. Les suivantes tombent à ce jour-là du mois ; un samedi ou un dimanche glisse au lundi.</p>
          </div>
          {lectureRythme && !lectureRythme.ok && rythme.moisTexte.trim() !== '' && <p className="message erreur">{lectureRythme.erreur}</p>}
          {effetRythme && <p className="message info">{effetRythme}</p>}
          <ErreurFormulaire message={erreur?.message ?? null} details={erreur instanceof ApiError ? erreur.details : undefined} />
        </Panneau>
      )}

      {modification && detail && (
        <Panneau
          titre="Modifier la tournée"
          sousTitre="N° de commande et remarque : toutes les interventions de la tournée les lisent. Le nom et la durée ne changent pas les interventions déjà créées ; les machines et les habituels n’agissent que sur celles à venir."
          onFermer={() => setModification(null)}
          actions={
            <>
              <button type="button" className="secondaire" disabled={enCours} onClick={() => setModification(null)}>
                Ne rien changer
              </button>
              <button type="button" disabled={enCours} onClick={enregistrer}>
                Enregistrer
              </button>
            </>
          }
        >
          <div className="champ">
            <label htmlFor="tournee-modif-label">Nom de la tournée</label>
            <input id="tournee-modif-label" type="text" maxLength={120} value={modification.label} onChange={(e) => setModification({ ...modification, label: e.target.value })} />
          </div>
          <div className="champ">
            <label htmlFor="tournee-modif-commande">N° de commande du client</label>
            <input id="tournee-modif-commande" type="text" maxLength={60} value={modification.commande} onChange={(e) => setModification({ ...modification, commande: e.target.value })} />
          </div>
          <div className="champ">
            <label htmlFor="tournee-modif-duree">Durée prévue de chaque intervention</label>
            <input
              id="tournee-modif-duree"
              type="text"
              inputMode="decimal"
              value={modification.dureeTexte}
              placeholder="8, 7,5, 1h30, 45 min"
              onChange={(e) => setModification({ ...modification, dureeTexte: e.target.value })}
            />
            <p className="aide">La durée a servi à créer les interventions : celles qui existent déjà la gardent.</p>
          </div>
          <div className="champ">
            <label htmlFor="tournee-modif-remarque">Remarque de la tournée</label>
            <textarea id="tournee-modif-remarque" rows={3} maxLength={1000} value={modification.remarque} onChange={(e) => setModification({ ...modification, remarque: e.target.value })} />
          </div>
          <div className="champ">
            <label className="case-a-cocher">
              <input
                type="checkbox"
                checked={modification.sansDeplacement}
                onChange={(e) => setModification((c) => (c ? { ...c, sansDeplacement: e.target.checked } : c))}
              />{' '}
              Pas de déplacement facturé pour cette tournée
            </label>
            <p className="aide">Vaut pour toutes les interventions de la tournée, déjà créées ou à venir. Un « déplacement en plus » ajouté sur une intervention reste facturé.</p>
          </div>
          <fieldset className="champ tournee-composition tournee-machines">
            <legend>Machines de la tournée</legend>
            {choixMachines.map((m) => (
              <label key={m.id} className="case-a-cocher">
                <input
                  type="checkbox"
                  checked={modification.assetIds.includes(m.id)}
                  onChange={(e) =>
                    setModification((c) =>
                      c ? { ...c, assetIds: e.target.checked ? [...c.assetIds, m.id] : c.assetIds.filter((x) => x !== m.id) } : c,
                    )
                  }
                />{' '}
                {m.nom}
              </label>
            ))}
          </fieldset>
          <fieldset className="champ tournee-composition tournee-machines">
            <legend>Techniciens habituels</legend>
            {choixTechniciens.length === 0 && <p className="aide">Aucun technicien dans l’annuaire.</p>}
            {choixTechniciens.map((t) => (
              <label key={t.id} className="case-a-cocher">
                <input
                  type="checkbox"
                  checked={modification.technicianIds.includes(t.id)}
                  onChange={(e) =>
                    setModification((c) =>
                      c ? { ...c, technicianIds: e.target.checked ? [...c.technicianIds, t.id] : c.technicianIds.filter((x) => x !== t.id) } : c,
                    )
                  }
                />{' '}
                {t.name}
                {t.archived && <span className="aide"> (archivé)</span>}
              </label>
            ))}
            <p className="aide">Facultatif : sans habituel, chaque intervention attend son équipe.</p>
          </fieldset>
          {effetComposition && <p className="message info">{effetComposition}</p>}
          <ErreurFormulaire message={erreur?.message ?? null} details={erreur instanceof ApiError ? erreur.details : undefined} />
        </Panneau>
      )}
    </div>
  );
}
